import Plot from 'react-plotly.js';
import {Box, Paper, Typography, useTheme} from "@mui/material";
import {formatCurrency, formatNumber, formatPercent} from "../utils/formatters";

const BUBBLE_CHROME_H = 88;

const SEGMENT_COLORS = {
    defend: '#2E7D32',
    grow: '#42A5F5',
    optimize: '#FB8C00',
    review_and_fix: '#E53935',
};

const SEGMENT_LABELS = {
    defend: 'Defend',
    grow: 'Grow',
    optimize: 'Optimize',
    review_and_fix: 'Review/Fix',
};

export const SegmentationBubbleChart = ({entities, selectedSegment, cardHeight = null}) => {
    const theme = useTheme();
    const emptyPaperStyle = {
        ...theme.palette.contentPanel,
        padding: 20,
        height: cardHeight ?? 420,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
    };

    if (!entities || entities.length === 0) {
        return (
            <Paper style={emptyPaperStyle}>
                <Typography color="textSecondary">No entity data to plot</Typography>
            </Paper>
        );
    }

    const visible = selectedSegment
        ? entities.filter((entity) => entity.segment === selectedSegment)
        : entities;

    const maxVariableMarginAbs = Math.max(
        1,
        ...visible.map((entity) => Math.abs(entity.variable_margin || 0))
    );

    const sizeFor = (entity) => {
        const margin = Math.abs(entity.variable_margin || 0);
        const scaled = Math.sqrt(margin / maxVariableMarginAbs) * 40;
        return Math.max(6, scaled);
    };

    const segments = Array.from(new Set(visible.map((entity) => entity.segment)));
    const traces = segments.map((segment) => {
        const rows = visible.filter((entity) => entity.segment === segment);
        return {
            name: SEGMENT_LABELS[segment] || segment,
            mode: 'markers',
            x: rows.map((row) => row.volume),
            y: rows.map((row) => (row.variable_margin_percent || 0) * 100),
            customdata: rows.map((row) => ([
                row.name,
                SEGMENT_LABELS[row.segment] || row.segment,
                formatNumber(row.volume),
                formatPercent(row.variable_margin_percent, 1),
                formatCurrency(row.variable_margin, true),
            ])),
            hovertemplate:
                '<b>%{customdata[0]}</b><br>' +
                'Segment: %{customdata[1]}<br>' +
                'Volume: %{customdata[2]}<br>' +
                'VM%: %{customdata[3]}<br>' +
                'Variable Margin: %{customdata[4]}' +
                '<extra></extra>',
            marker: {
                size: rows.map(sizeFor),
                color: SEGMENT_COLORS[segment] || '#607D8B',
                line: {color: '#00000030', width: 1},
                sizemode: 'diameter'
            }
        };
    });

    const plotHeight = cardHeight != null
        ? Math.max(280, cardHeight - BUBBLE_CHROME_H)
        : 420;

    const paperStyle = {
        ...theme.palette.contentPanel,
        padding: 12,
        ...(cardHeight != null
            ? {
                height: cardHeight,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                minHeight: 0
            }
            : {})
    };

    return (
        <Paper style={paperStyle}>
            <Typography variant="subtitle1" style={{fontWeight: 600, padding: '4px 8px'}}>
                VM Segmentation Map
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{padding: '0 8px'}}>
                Bubble size = variable margin · X = volume · Y = variable margin %
            </Typography>
            <Box sx={{flex: cardHeight != null ? 1 : undefined, minHeight: 0, width: '100%'}}>
                <Plot
                    data={traces}
                    layout={{
                        autosize: true,
                        height: plotHeight,
                        margin: {t: 20, b: 50, l: 50, r: 20},
                        xaxis: {title: {text: 'Volume'}, rangemode: 'tozero'},
                        yaxis: {title: {text: 'Variable Margin %'}, rangemode: 'tozero'},
                        legend: {orientation: 'v', x: 1.02, y: 1},
                        hovermode: 'closest'
                    }}
                    useResizeHandler
                    style={{width: '100%'}}
                    config={{displayModeBar: false}}
                />
            </Box>
        </Paper>
    );
};
